import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { fetchUserStats, fetchEventStats } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import UsersTab from '../components/UsersTab';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [userData, setUserData] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    bySubscriptionType: {},
    byRole: {},
    recentRegistrations: [],
    registrations_by_month: {},
    events_by_user: {}
  });
  const [eventStats, setEventStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    byCategory: {},
    byState: {},
    recentEvents: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get authentication data from context
  const { isAuthenticated, user } = useAuth();
  
  // Get token from localStorage
  const token = localStorage.getItem('token');

  // Check if user has admin rights
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      setError('You do not have permission to access the admin panel');
    }
  }, [isAuthenticated, user]);

  // Load user statistics
  useEffect(() => {
    async function loadUserStats() {
      try {
        setLoading(true);
        const response = await fetchUserStats(token);
        
        if (!response.ok) {
          throw new Error(`Error loading statistics: ${response.status}`);
        }
        
        const data = await response.json();
        setUserData({
          totalUsers: data.total_users,
          activeSubscriptions: data.active_subscriptions,
          bySubscriptionType: data.by_subscription_type,
          byRole: data.by_role,
          recentRegistrations: data.recent_registrations,
          registrations_by_month: data.registrations_by_month || {},
          events_by_user: data.events_by_user || {}
        });
        setLoading(false);
      } catch (err) {
        console.error('Error loading user statistics:', err);
        setError(err.message);
        setLoading(false);
      }
    }
    
    if (token && isAuthenticated && user?.role === 'admin') {
      loadUserStats();
    }
  }, [token, isAuthenticated, user]);

  // Load event statistics
  useEffect(() => {
    async function loadEventStats() {
      try {
        setLoading(true);
        const response = await fetchEventStats(token);
        
        if (!response.ok) {
          throw new Error(`Error loading event statistics: ${response.status}`);
        }
        
        const data = await response.json();
        setEventStats({
          totalEvents: data.total_events,
          activeEvents: data.active_events,
          byCategory: data.by_category,
          byState: data.by_state,
          recentEvents: data.recent_events
        });
        setLoading(false);
      } catch (err) {
        console.error('Error loading event statistics:', err);
        setError(err.message);
        setLoading(false);
      }
    }
    
    if (token && isAuthenticated && user?.role === 'admin' && activeTab === 'activity') {
      loadEventStats();
    }
  }, [token, activeTab, isAuthenticated, user]);

  // Prepare subscription data for charts
  const prepareSubscriptionData = () => {
    return Object.entries(userData.bySubscriptionType).map(([name, value]) => ({
      name,
      value
    }));
  };

  // Prepare registration data from API instead of hardcoded values
  const registrationData = userData.registrations_by_month 
    ? Object.entries(userData.registrations_by_month).map(([name, value]) => ({
        name,
        count: value
      }))
    : [];

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Authentication check
  if (!isAuthenticated || (user && user.role !== 'admin')) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-red-600">
          {!isAuthenticated 
            ? "Please log in to access the admin panel" 
            : "You do not have permission to access the admin panel"}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (loading && activeTab === 'overview') {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading data...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>
      
      {/* Tabs */}
      <div className="flex border-b mb-6">
        <button 
          className={`px-4 py-2 ${activeTab === 'overview' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-600'}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`px-4 py-2 ${activeTab === 'users' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-600'}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`px-4 py-2 ${activeTab === 'subscriptions' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-600'}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          Subscriptions
        </button>
        <button 
          className={`px-4 py-2 ${activeTab === 'activity' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-600'}`}
          onClick={() => setActiveTab('activity')}
        >
          Activity
        </button>
      </div>
      
      {/* Overview */}
      {activeTab === 'overview' && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded shadow">
              <h3 className="text-gray-500 text-sm">Total Users</h3>
              <p className="text-2xl font-bold">{userData.totalUsers}</p>
            </div>
            <div className="bg-white p-4 rounded shadow">
              <h3 className="text-gray-500 text-sm">Active Subscriptions</h3>
              <p className="text-2xl font-bold">{userData.activeSubscriptions}</p>
            </div>
            <div className="bg-white p-4 rounded shadow">
              <h3 className="text-gray-500 text-sm">Activation Percentage</h3>
              <p className="text-2xl font-bold">
                {userData.totalUsers > 0 
                  ? Math.round((userData.activeSubscriptions / userData.totalUsers) * 100) 
                  : 0}%
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-4 rounded shadow">
              <h3 className="font-bold mb-4">Monthly Registrations</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={registrationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded shadow">
              <h3 className="font-bold mb-4">Subscriptions by Type</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={prepareSubscriptionData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {prepareSubscriptionData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-bold mb-4">Recent Registrations</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b text-left">ID</th>
                    <th className="py-2 px-4 border-b text-left">Name</th>
                    <th className="py-2 px-4 border-b text-left">Email</th>
                    <th className="py-2 px-4 border-b text-left">Registration Date</th>
                    <th className="py-2 px-4 border-b text-left">Subscription</th>
                  </tr>
                </thead>
                <tbody>
                  {userData.recentRegistrations.map(user => (
                    <tr key={user.id}>
                      <td className="py-2 px-4 border-b">{user.id}</td>
                      <td className="py-2 px-4 border-b">{user.name}</td>
                      <td className="py-2 px-4 border-b">{user.email}</td>
                      <td className="py-2 px-4 border-b">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-4 border-b">
                        <span className={`px-2 py-1 rounded text-xs ${user.subscription_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {user.subscription_type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Users - using the UsersTab component */}
      {activeTab === 'users' && (
        <div>
          <UsersTab />
          
          {/* Events per user section */}
          {userData.events_by_user && Object.keys(userData.events_by_user).length > 0 && (
            <div className="bg-white p-4 rounded shadow mb-6 mt-6">
              <h3 className="font-bold mb-4">Events per User</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr>
                      <th className="py-2 px-4 border-b text-left">ID</th>
                      <th className="py-2 px-4 border-b text-left">Name</th>
                      <th className="py-2 px-4 border-b text-left">Email</th>
                      <th className="py-2 px-4 border-b text-left">Events Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(userData.events_by_user)
                      .sort((a, b) => b.event_count - a.event_count) // Sort by event count (highest first)
                      .map(user => (
                        <tr key={user.id}>
                          <td className="py-2 px-4 border-b">{user.id}</td>
                          <td className="py-2 px-4 border-b">{user.name}</td>
                          <td className="py-2 px-4 border-b">{user.email}</td>
                          <td className="py-2 px-4 border-b">
                            <span className={`px-2 py-1 rounded text-xs ${
                              user.event_count > 5 ? 'bg-green-100 text-green-800' : 
                              user.event_count > 0 ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {user.event_count}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Events distribution chart */}
          {userData.events_by_user && Object.keys(userData.events_by_user).length > 0 && (
            <div className="bg-white p-4 rounded shadow mb-6">
              <h3 className="font-bold mb-4">Events Distribution by User</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={Object.values(userData.events_by_user)
                      .filter(user => user.event_count > 0)
                      .sort((a, b) => b.event_count - a.event_count)
                      .slice(0, 10)} // Show top 10 users
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="event_count" fill="#8884d8" name="Number of Events" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Subscriptions */}
      {activeTab === 'subscriptions' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-bold mb-4">Subscription Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={prepareSubscriptionData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({name, value}) => `${name}: ${value}`}
                  >
                    {prepareSubscriptionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-bold mb-4">Subscription Statistics</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm text-gray-500">Activation Rate</h4>
                <p className="text-lg font-bold">
                  {userData.totalUsers > 0 
                    ? Math.round((userData.activeSubscriptions / userData.totalUsers) * 100) 
                    : 0}%
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full" 
                    style={{ width: `${Math.round((userData.activeSubscriptions / userData.totalUsers) * 100)}%` }}
                  ></div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm text-gray-500">Distribution by Plan</h4>
                <div className="space-y-2 mt-2">
                  {Object.entries(userData.bySubscriptionType).map(([type, count]) => (
                    <div key={type}>
                      <div className="flex justify-between">
                        <span className="text-sm">{type}</span>
                        <span className="text-sm font-medium">
                          {count} ({userData.totalUsers > 0 ? Math.round((count / userData.totalUsers) * 100) : 0}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            type === 'free' ? 'bg-blue-500' : 
                            type === 'basic' ? 'bg-green-500' : 'bg-purple-500'
                          }`} 
                          style={{ width: `${userData.totalUsers > 0 ? Math.round((count / userData.totalUsers) * 100) : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm text-gray-500">Upcoming Subscription Expirations</h4>
                <div className="mt-2 space-y-2">
                  {userData.recentRegistrations
                    .filter(user => user.subscription_status === 'active' && user.subscription_end)
                    .sort((a, b) => new Date(a.subscription_end) - new Date(b.subscription_end))
                    .slice(0, 5)
                    .map(user => (
                      <div key={user.id} className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">{user.subscription_type}</p>
                          <p className="text-xs text-gray-500">until {new Date(user.subscription_end).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Activity */}
      {activeTab === 'activity' && (
        <div>
          {loading ? (
            <div className="text-center py-4">Loading event data...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="text-gray-500 text-sm">Total Events</h3>
                  <p className="text-2xl font-bold">{eventStats.totalEvents}</p>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="text-gray-500 text-sm">Active Events</h3>
                  <p className="text-2xl font-bold">{eventStats.activeEvents}</p>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="text-gray-500 text-sm">Active Percentage</h3>
                  <p className="text-2xl font-bold">
                    {eventStats.totalEvents ? Math.round((eventStats.activeEvents / eventStats.totalEvents) * 100) : 0}%
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded shadow mb-6">
                <h3 className="font-bold mb-4">Recent Events</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-4 border-b text-left">ID</th>
                        <th className="py-2 px-4 border-b text-left">Name</th>
                        <th className="py-2 px-4 border-b text-left">Category</th>
                        <th className="py-2 px-4 border-b text-left">Date</th>
                        <th className="py-2 px-4 border-b text-left">Status</th>
                        <th className="py-2 px-4 border-b text-left">Guests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventStats.recentEvents.map(event => (
                        <tr key={event.id}>
                          <td className="py-2 px-4 border-b">{event.id}</td>
                          <td className="py-2 px-4 border-b">{event.name}</td>
                          <td className="py-2 px-4 border-b">{event.category || 'Uncategorized'}</td>
                          <td className="py-2 px-4 border-b">
                            {new Date(event.start_time).toLocaleDateString()}
                          </td>
                          <td className="py-2 px-4 border-b">
                            <span className={`px-2 py-1 rounded text-xs ${
                              event.state === 'active' ? 'bg-green-100 text-green-800' : 
                              event.state === 'idea' ? 'bg-blue-100 text-blue-800' :
                              event.state === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {event.state}
                            </span>
                          </td>
                          <td className="py-2 px-4 border-b">
                            {event.total_guests} (checked in: {event.checked_in_guests})
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="font-bold mb-4">Events by Category</h3>
                  <div className="space-y-3">
                    {Object.entries(eventStats.byCategory).map(([category, count], index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full mr-3">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span>{category}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                            <div 
                              className="bg-blue-600 h-1 rounded-full" 
                              style={{ 
                                width: `${(count / Math.max(...Object.values(eventStats.byCategory).map(Number))) * 100}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="font-bold mb-4">Events by Status</h3>
                  <div className="space-y-3">
                    {Object.entries(eventStats.byState).map(([state, count], index) => (
                      <div key={index} className="flex items-center">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full mr-3 ${
                          state === 'active' ? 'bg-green-100 text-green-800' : 
                          state === 'idea' ? 'bg-blue-100 text-blue-800' :
                          state === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span>{state}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                            <div 
                              className={`h-1 rounded-full ${
                                state === 'active' ? 'bg-green-600' : 
                                state === 'idea' ? 'bg-blue-600' :
                                state === 'cancelled' ? 'bg-red-600' :
                                'bg-gray-600'
                              }`}
                              style={{ 
                                width: `${(count / Math.max(...Object.values(eventStats.byState).map(Number))) * 100}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;